import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MODAL, ModalService } from '../../services/modal.service';
import { UserService } from '../../services/user.service';
import { environment } from '../../../environments/environment';
import { CurrentService } from '../../services/current.service';
import { PrintService } from '../../services/print.service';
import { TranslateService } from '@ngx-translate/core';
import * as JsBarcode from 'jsbarcode';
import { Assistance } from 'src/app/model/assistance';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { GTM } from 'src/app/services/gtm.service';
import { EventDataTransferService } from 'src/app/services/event-data-transfer.service';
import { CONTEXT } from 'src/app/enum/context';
import { PriceHelper } from 'src/app/services/helpers/price-helper';

@Component({
  selector: 'ng-hbm-bdi-products-list',
  templateUrl: './products-list.component.html'
})
export class ProductsListComponent implements OnInit {
  faWarehouse = faWarehouse;

  public flattenBom: any;
  public beautifiedBom: any;
  public customerInfos: any;
  public projectName: string;

  public devEnvironment: boolean;
  public isVendor: boolean;
  public projectIsSaved: boolean;
  public projectIsLocked: boolean = false;
  public printLoading: boolean;
  public transferLoading: boolean;
  public bareCodes: any;
  public assistances: Assistance[];
  public loader: boolean = false;
  public anyProductNotInStock: boolean = false;
  public qrCodeData: string;
  public phoneNumber: string;
  private projectAmount: number;
  public disclaimer: string;
  @ViewChildren('barcodeSVG') barcodeSVGElements: QueryList<ElementRef>;

  constructor(
    public modalService: ModalService,
    public customer: UserService,
    public current: CurrentService,
    private print: PrintService,
    private translate: TranslateService,
    private gtm: GTM,
    private eventDataTransfert: EventDataTransferService
  ) {
    this.customerInfos = {
      name: "(N/A)",
      id: "-"
    };

    let disclaimerContext = '';
    switch (this.customer.context) {
      case CONTEXT.KITCHEN: disclaimerContext = this.translate.instant('popins.products_list.aside.disclaimer2Kitchen'); break;
      case CONTEXT.STORAGE: disclaimerContext = this.translate.instant('popins.products_list.aside.disclaimer2Storage'); break;
      case CONTEXT.BATHROOM: disclaimerContext = this.translate.instant('popins.products_list.aside.disclaimer2Bathroom'); break;
    }

    this.disclaimer = `${this.translate.instant('popins.products_list.aside.disclaimer1')} ${disclaimerContext} ${this.translate.instant('popins.products_list.aside.disclaimer3')}`;

    this.beautifiedBom = modalService.currentDatas.bom;
    this.flattenBom = modalService.currentDatas.flattenBom;
    this.qrCodeData = this.getQrCodeData();

    if (this.customer.userDatas) {
      this.isVendor = this.customer.userDatas.vendor;
      if (this.customer.userDatas.assistances) {
        this.assistances = this.customer.userDatas.assistances;
      }

      if (this.customer.userDatas.otherAccount) {
        this.customerInfos.name = this.customer.userDatas.otherAccount.completeName;
        this.customerInfos.id = this.customer.userDatas.otherAccount.magentoId;
      } else {
        this.customerInfos.name = this.isVendor ? this.getShorterName() : this.customer.userDatas.completeName;
        this.customerInfos.id = this.customer.userDatas.magentoId;
      }
    } else {
      this.isVendor = false;
    }

    this.projectIsSaved = current.project !== null && current.project !== undefined;
    if (this.projectIsSaved) {
      this.projectIsLocked = current.project.locked;
    }

    this.devEnvironment = !environment.production;

    if (this.isVendor && this.customer.userDatas.storeId !== null) {
      this.anyProductNotInStock = this.flattenBom.products.some(b => b.stockAvailability.find(s => s.stock === 0 && s.storeId === this.customer.userDatas.storeId.toString()) || b.stockAvailability.length == 0);
    }
  }

  ngOnInit(): void {
    this.projectAmount = this.beautifiedBom.totalPrice.toFixed(2);
    this.gtm.trackEvent('click', 'products-list-' + this.customer.context.toLowerCase(), 'open', { 'project_amount': this.projectAmount });
    this.phoneNumber = this.getPhone();
  }

  ngAfterViewInit() {
    this.generateBarcodes();
  }

  getShorterName() {
    return (this.customer.userDatas.firstName ? this.customer.userDatas.firstName + '.' : '')
      + (this.customer.userDatas.lastName ? this.customer.userDatas.lastName.toUpperCase()[0] : '')
  }

  close() {
    this.gtm.trackEvent('click', 'products-list-' + this.customer.context.toLowerCase(), 'close', { 'project_amount': this.projectAmount });
    this.modalService.close();
  }

  order() {
    this.modalService.display(MODAL.ORDER_MESSAGE, {
      bom: this.beautifiedBom,
      flattenBom: this.flattenBom
    });
  }

  getOldPrice(productRow: any) {
    if (productRow.price.current.type === 'reduced') {
      return productRow.price.regular.value;
    }
    return undefined;
  }  

  getUnitPriceHt(productRow: any, productBlockRow: any) {
    return PriceHelper.getUnitPriceHtFromBom(productRow, productBlockRow);
  }

  getPriceHt(productRow, productBlockRow) {
    return PriceHelper.getPriceHtFromBom(productRow, productBlockRow);
  }

  getTva(productRow: any, productBlockRow: any) {
    if (productRow.clientMetadata?.TVA) {
      return productRow.clientMetadata.TVA;
    }

    return PriceHelper.getBlockTvaFromBom(productBlockRow);
  }

  getTotalTva(beautifiedBom: any): number {
    return beautifiedBom.totalPrice + this.getAssistanceTotalPrice() - this.getTotalPriceHt(beautifiedBom);
  }

  getBlockPriceHt(productBlock: any): number {
    return PriceHelper.getBlockPriceHtFromBom(productBlock);
  }

  getTotalPriceHt(beautifiedBom: any): number {
    return beautifiedBom.blocks.reduce(
      (acc, val) => acc + this.getBlockPriceHt(val), 0
    );
  }

  openAssistances() {
    this.modalService.display(MODAL.ASSISTANCE, {
      existingAssistances: this.assistances,
      bom: this.beautifiedBom,
      flattenBom: this.flattenBom
    });
  }

  isAddServiceDisabled() {
    if (this.assistances) {
      if (this.assistances.length == 2) {
        return true;
      }
    }
    return false;
  }

  getAssistanceTotalPrice(): number {
    if (this.assistances) {
      return this.assistances.reduce((accumulator, assistance) => {
        return accumulator + assistance.price;
      }, 0);
    }
    return 0;
  }

  generateBarcodes() {
    let index = 0;

    for (const block of this.beautifiedBom.blocks) {
      for (const product of block.rows) {
        const ean = product.reference;
        const element = this.barcodeSVGElements.toArray()[index];

        JsBarcode(element.nativeElement)
          .options({ font: "OCR-B" })
          .EAN13(ean, { fontSize: 18, textMargin: 0, height: 40 })
          .render();

        index++;
      }
    }
  }

  getQrCodeData(): string {
    let _rightNow = new Date();
    _rightNow.setDate(_rightNow.getDate() + 30); //ajouter 30 jours
    let _newDateStr = _rightNow.toISOString().slice(0, 10).split("-");
    let stringData: string = "ID-" + this.current.project?.id + "#" + _newDateStr[0] + "-" + _newDateStr[1] + "-" + _newDateStr[2] + "#";
    for (const product of this.flattenBom.products.slice(0, 100)) {
      stringData += product.reference + "," + product.quantity + "," + product.price.current.value.toFixed(2) + "#";
    }
    return stringData;
  }

  selectedStoreAvailability(ean: string): boolean {
    let product = this.flattenBom.products.find(f => f.reference === ean);

    let stock = this.selectedStoreStock(ean, this.customer.userDatas.storeId);
    return stock >= product.quantity;
  }

  selectedStoreStock(ean: string, storeId: string): number {
    let product = this.flattenBom.products.find(f => f.reference === ean);
    if (product) {
      let store = product.stockAvailability.find(s => s.storeId == storeId)
      if (store) {
        return store.stock;
      }
    }
    return 0;
  }

  dcStock(ean: string): number {
    return this.selectedStoreStock(ean, environment.bdi.distributionCenterStoreId);
  }

  displayAllStoreStock(ean: string): void {
    let product = this.flattenBom.products.find(f => f.reference === ean);
    this.modalService.display(MODAL.STORE_STOCK, {
      storeStock: product.stockAvailability,
      beautifiedBom: this.beautifiedBom,
      flattenBom: this.flattenBom,
      ean: ean,
      onClose: () => {
        this.modalService.close();
      }
    });
  }

  getPhone(): string {
    if (this.customer.userDatas?.phone) {
      return this.customer.userDatas?.phone;
    } else if (this.customer.userDatas?.otherAccount?.entryPhone) {
      return this.customer.userDatas?.otherAccount?.entryPhone;
    } else if (this.customer.userDatas?.otherAccount?.phone) {
      return this.customer.userDatas?.otherAccount?.phone;
    }
    return undefined;
  }

  updatePhone(event: any) {
    if (this.customer.userDatas?.otherAccount) {
      this.customer.userDatas.otherAccount.entryPhone = event.target.value;
    } else if (this.customer.userDatas) {
      this.customer.userDatas.phone = event.target.value;
    }
    this.phoneNumber = event.target.value;
  }

  printList() {
    this.gtm.trackEvent('click', 'products-list-' + this.customer.context.toLowerCase(), 'print', { 'project_amount': this.projectAmount });
    this.eventDataTransfert.transmitData("print", this.projectAmount);

    this.printLoading = true;

    this.print.reset();
    let title: string = this.translate.instant('popins.products_list.project')
      + (this.current.project ? this.current.project.name : this.translate.instant('popins.price_info.placeholder_project'));
    this.print.addHeader(title).then(() => {
      this.print.addDiv(document.querySelector(".qrcode").cloneNode(true) as HTMLDivElement).then(() => {
        this.print.addText("", "mt-4");

        if (this.customer.isLogged) {
          this.print.addText(this.translate.instant('popins.products_list.name') + this.customerInfos.name, "my-3");
          let phone = this.getPhone();
          this.print.addText(this.translate.instant('popins.products_list.phone') + (phone || ""), "my-3");

          if (this.customer.userDatas?.otherAccount?.email) {
            this.print.addText(this.translate.instant('popins.products_list.email') + this.customer.userDatas.otherAccount.email, "my-3");
          }

          if (this.customer.userDatas?.vendor) {
            this.print.addText(
              this.translate.instant('popins.products_list.vendor')
              + (this.isVendor ? this.getShorterName() : this.customer.userDatas.completeName)
              + (this.customer.userDatas?.storeId ? " (" + this.customer.userDatas.storeId + ")" : ""), "my-3");
          }
        }

        let _rightNow = new Date();
        let _dateStr = _rightNow.toISOString().slice(0, 10).split("-");
        this.print.addText(this.translate.instant('popins.products_list.date') + _dateStr[2] + "/" + _dateStr[1] + "/" + _dateStr[0], "mt-3");

        _rightNow.setDate(_rightNow.getDate() + 30); //ajouter 30 jours
        let _newDateStr = _rightNow.toISOString().slice(0, 10).split("-");
        this.print.addText(this.translate.instant('popins.products_list.date30') + _newDateStr[2] + "/" + _newDateStr[1] + "/" + _newDateStr[0], "mt-3 mb-5");

        this.print.addDivPrice(document.querySelector(".price-table-container").cloneNode(true) as HTMLDivElement);

        this.print.addText("", "mt-5");

        this.print.addTable(document.querySelector(".table.product-table").cloneNode(true) as HTMLTableElement, this.isVendor).then(() => {
          this.print.addText("", "mt-5");
          let _conditions: string[] = this.translate.instant('popins.products_list.aside.generalConditions').split("<br/>");
          _conditions.forEach(p => this.print.addText(p, "my-3"));
          this.print.addText(this.translate.instant('popins.products_list.aside.generalConditionTable'), "my-3");
          let signatureDiv = document.querySelector(".signature-container").cloneNode(true) as HTMLDivElement;
          signatureDiv.style.pageBreakInside = "avoid";
          this.print.addDiv(signatureDiv);

          this.print.print();
          this.printLoading = false;
        });
      });
    });
  }


  doTheRest() {
    
  }
}
